import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Calendar, Info } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import useDividendSchedule from '@/hooks/useDividendSchedule';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RecentEventsCard — Round 95c (Jun 2026).
 *
 * Standalone Recent Events card extracted from `MassiveInsightsTab` so
 * the Dividend Info tab can render it directly under the Streak Risk
 * Chart (per user spec). Owns its own fetch against
 * `/api/stocks/{ticker}/events?limit=10` so the host tab doesn't need
 * to plumb data through. Self-hides when no events are returned.
 *
 * Massive surfaces:
 *   • dividend_type — CD (regular cash), SC (special), LT (long-term
 *     cap gain), ST (short-term cap gain). Specials + cap-gains get
 *     an amber tone so they pop against the green regular dividends.
 *   • frequency — 1=annual, 2=semi, 4=quarterly, 12=monthly, 52=weekly,
 *     0=irregular/one-time. Surfaced as a compact " · Qtr/Mo/Wk" tag.
 *
 * Round 95y (Jun 2026) — admin-only provenance badge. When the backend
 * supplemented Massive's sparse dividend events with the local 24-month
 * cache (newly-added ticker whose Massive index lags), an amber
 * "backfilled" pill appears next to the title. End-users never see
 * this — the `supplemented` flag is admin-gated server-side.
 */

const fmtEventDate = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  } catch { return d; }
};

function eventLabel(ev) {
  const t = ev.type;
  const data = ev.data || {};
  if (t === 'dividend') {
    const amt = data.cash_amount;
    const freq = data.frequency;
    const freqLabel = freq === 365 ? 'Daily' : freq === 4 ? 'Qtr' : freq === 12 ? 'Mo' : freq === 52 ? 'Wk' : freq === 2 ? 'Semi' : freq === 1 ? 'Ann' : '';
    const dt = (data.dividend_type || '').toUpperCase();
    const typeTag = dt === 'SC' ? ' · SPECIAL'
      : dt === 'LT' ? ' · LT CAP GAIN'
      : dt === 'ST' ? ' · ST CAP GAIN'
      : '';
    return amt != null
      ? `Dividend $${Number(amt).toFixed(4)}${freqLabel ? ` · ${freqLabel}` : ''}${typeTag}`
      : `Dividend${typeTag}`;
  }
  if (t === 'split') return `Split ${data.split_to || '?'}:${data.split_from || '?'}`;
  if (t === 'ticker_change') return 'Ticker symbol change';
  if (t === 'name_change') return 'Name change';
  return t || 'Event';
}

function eventTone(type, data) {
  if (type === 'dividend') {
    const dt = (data?.dividend_type || '').toUpperCase();
    if (dt === 'SC' || dt === 'LT' || dt === 'ST') return 'text-amber-700 dark:text-amber-300';
    return 'text-emerald-700 dark:text-emerald-300';
  }
  if (type === 'split') return 'text-amber-700 dark:text-amber-300';
  return 'text-violet-700 dark:text-violet-300';
}

export default function RecentEventsCard({ ticker }) {
  const [other, setOther] = useState([]);
  const [supplemented, setSupplemented] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { data: schedule, loading: schedLoading } = useDividendSchedule(ticker);
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';

  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    axios.get(`${API}/stocks/${ticker}/events?limit=10`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        setOther((r.data?.events || []).filter((ev) => ev.type !== 'dividend'));
        setSupplemented(!!r.data?.supplemented);
      })
      .catch(() => { if (!cancelled) setOther([]); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [ticker]);

  // Dividend rows come from the same Massive pull that feeds the Last / Next
  // Payment cards, so the two sections can never disagree.
  const events = useMemo(() => {
    const divs = (schedule?.history || []).map((h) => ({
      type: 'dividend',
      date: h.ex_date,
      data: { cash_amount: h.amount, frequency: h.frequency, dividend_type: h.dividend_type, pay_date: h.pay_date },
    }));
    return [...divs, ...other].sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 10);
  }, [schedule, other]);

  if (!loaded || schedLoading || events.length === 0) return null;

  return (
    <div className="rounded-lg border bg-card p-3" data-testid="dividend-info-recent-events-card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-fuchsia-500 dark:text-fuchsia-400" />
          <h4 className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400">
            Recent Events
          </h4>
          {isAdmin && supplemented && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30"
              title="Admin: Massive's index lagged for this ticker — older payments backfilled from local 24-month cache. Will self-heal once Massive catches up."
              data-testid="recent-events-admin-supplemented-badge"
            >
              <Info className="w-2.5 h-2.5" />
              backfilled
            </span>
          )}
        </div>
        <span className="text-[10px] font-bold text-muted-foreground">Newest first</span>
      </div>
      <ul className="space-y-1.5">
        {events.map((ev, i) => (
          <li
            key={`${ev.type}-${ev.date}-${i}`}
            className="flex items-center justify-between gap-2 text-sm py-1 border-b border-border/20 last:border-0"
            data-testid={`dividend-info-event-${i}`}
          >
            <span className="text-muted-foreground font-mono text-xs shrink-0">{fmtEventDate(ev.date)}</span>
            <span className={`font-medium ${eventTone(ev.type, ev.data)} text-right flex-1 truncate`}>{eventLabel(ev)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
